export type MmaCsvRow = {
    imageFilename: string
    timeMicros: number
    sensorLatitude: number
    sensorLongitude: number
    sensorAltitudeWgs84: number
    sensorAltitudeMsl: number
    sensorAltitudeAgl: number
    sensorRoll: number
    sensorPitch: number
    sensorYaw: number
    cameraMake: string
    cameraModel: string
    focalLengthMm: number
    hFov: number
    vFov: number
    imageWidth: number
    imageHeight: number
    gain: number
    integrationTimeMicros: number
    slantRange: number
    geolocationCorrectionLevel: number
    radialDistortionK1: number
    radialDistortionK2: number
}

const REQUIRED_COLUMN = "Image Filename"

function parseNum(v: string): number {
    const n = parseFloat(v)
    return isNaN(n) ? 0 : n
}

/**
 * Parses one or more MMA telemetry CSVs and returns a lookup map keyed by
 * image filename stem (no path, no extension — matches an image file.name
 * without its extension).
 *
 * The CSV format has three header rows: column names, units, then data.
 * Multiple CSVs are merged. When the same image appears in more than one file,
 * the row with the higher Geolocation Correction Level wins — higher level means
 * the sensor attitude has been refined by additional registration (e.g. NIR band
 * co-registration), so it is the more accurate product.
 */
export async function parseMmaCsvFiles(csvFiles: File[]): Promise<Map<string, MmaCsvRow>> {
    const result = new Map<string, MmaCsvRow>()

    for (const file of csvFiles) {
        const text = await file.text()
        const rows = text.split(/\r?\n/)

        if (rows.length < 3) continue

        const headers = rows[0].split(",").map((h) => h.trim())
        const imgIdx = headers.indexOf(REQUIRED_COLUMN)
        if (imgIdx === -1) continue // not a valid MMA telemetry CSV

        const col = (row: string[], name: string) => {
            const i = headers.indexOf(name)
            return i === -1 ? "" : (row[i] ?? "").trim()
        }

        // Row 1 is units — skip. Data starts at row 2.
        for (let i = 2; i < rows.length; i++) {
            const row = rows[i].split(",")
            if (row.length < headers.length) continue

            const stem = col(row, "Image Filename").trim()
            if (!stem) continue

            const correctionLevel = parseNum(col(row, "Geolocation Correction Level"))
            const existing = result.get(stem)
            if (existing && existing.geolocationCorrectionLevel >= correctionLevel) continue

            result.set(stem, {
                imageFilename: stem,
                timeMicros: parseNum(col(row, "Time")),
                sensorLatitude: parseNum(col(row, "Sensor Latitude")),
                sensorLongitude: parseNum(col(row, "Sensor Longitude")),
                sensorAltitudeWgs84: parseNum(col(row, "Sensor WGS84 Altitude")),
                sensorAltitudeMsl: parseNum(col(row, "Sensor MSL Altitude")),
                sensorAltitudeAgl: parseNum(col(row, "Sensor AGL Altitude")),
                sensorRoll: parseNum(col(row, "Sensor Roll Angle")),
                sensorPitch: parseNum(col(row, "Sensor Pitch Angle")),
                sensorYaw: parseNum(col(row, "Sensor Yaw Angle")),
                cameraMake: "Overwatch", // Catchall for MMA
                cameraModel: col(row, "Camera Model Number") || "Unknown",
                focalLengthMm: parseNum(col(row, "Lens Focal Length")),
                hFov: parseNum(col(row, "Horizontal Field of View")),
                vFov: parseNum(col(row, "Vertical Field of View")),
                imageWidth: parseNum(col(row, "Image Width")),
                imageHeight: parseNum(col(row, "Image Height")),
                gain: parseNum(col(row, "Gain")),
                integrationTimeMicros: parseNum(col(row, "Image Integration Time")),
                slantRange: parseNum(col(row, "Slant Range")),
                geolocationCorrectionLevel: correctionLevel,
                radialDistortionK1: parseNum(col(row, "Radial Distortion k1")),
                radialDistortionK2: parseNum(col(row, "Radial Distortion k2")),
            })
        }
    }

    return result
}

/** Returns the filename stem used as the CSV key: strips path and extension. */
export function imageFileStem(filename: string): string {
    const base = filename.split("/").pop() ?? filename
    const dotIdx = base.lastIndexOf(".")
    return dotIdx === -1 ? base : base.slice(0, dotIdx)
}
